// Ansicht Training.
//
// Bettet JupyterLab same-origin in einem iframe ein (Default-Basispfad /jupyter/lab). Darüber eine
// knappe Erklär-Leiste: was die Notebooks tun (Soft-Sensor trainieren → ONNX exportieren → in die
// Modell-Registry veröffentlichen → als model-inference-Baustein im Signalgraph nutzbar) plus ein
// Knopf, der die In-App-Modell-Registry-Ansicht (RegistryView) öffnet. Diese lädt /api/v1/models
// über den authentifizierten API-Client (Bearer-Token wird angehängt) — früher war das ein roher
// Link nach /api/v1/models, dessen Browser-Navigation ohne Header lief und 401 auslöste (Issue #266).
//
// GPU-/Trainings-Pod (Issue #222, je Nutzer ab #314): die Leiste zeigt den EIGENEN Pod des
// angemeldeten Nutzers; der Knopf daneben öffnet den Editor, in dem er ihn selbst einrichtet
// (Provider, GPU-Typ/-Anzahl, Region, Limits, Zugangsdaten-Handle). Zugangsdaten stehen hier nur
// als OpenBao-Handle — ein Klartext-Geheimnis weist schon das Formular ab, das Backend erneut.
//
// Graceful, wenn die Umgebung nicht läuft: Das Backend liefert per GET /config, ob Training
// aktiviert ist und unter welchem Basispfad JupyterLab erreichbar ist. Ist es deaktiviert oder der
// Server nicht gestartet, zeigt die Ansicht einen sauberen Hinweis statt eines kaputten iframes.

import { useEffect, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n';
import { getConfig, getTrainingPod, getTrainingPodOptions, setTrainingPod } from '../api/client';
import type {
  PodProviderOptions,
  TrainingPodConfig,
  TrainingPodSummary,
  TrainingPodUpdate,
} from '../api/types';
import { Button, EmptyState, LabeledInput, LabeledSelect, PanelHeading } from '../components/ui';
import { RegistryView } from './RegistryView';
import { TrainingGoalsPanel } from './TrainingGoalsPanel';

// Zustand des Health-Checks: erst prüfen, dann bereit / deaktiviert / nicht erreichbar.
type Ready =
  | { kind: 'loading' }
  | { kind: 'ready'; labUrl: string }
  | { kind: 'disabled' }
  | { kind: 'unavailable' };

// Aus dem konfigurierten Basispfad die same-origin-URL der Lab-Oberfläche bilden. Der Default-Pfad
// ist /jupyter; die Lab-Oberfläche liegt darunter unter /lab. Doppelte Schrägstriche vermeiden.
function labUrlFromBase(base: string): string {
  const trimmed = base.replace(/\/+$/, '') || '/jupyter';
  return `${trimmed}/lab`;
}

// Kurzer Erreichbarkeits-Check der eingebetteten Lab-Oberfläche (same-origin, daher kein CORS). Ein
// HEAD auf die Lab-URL genügt: antwortet der Jupyter-Server (2xx/3xx), ist die Umgebung da. Fällt
// der Request mit einem Netzfehler aus, gilt sie als nicht gestartet.
async function labReachable(labUrl: string): Promise<boolean> {
  try {
    const res = await fetch(labUrl, { method: 'HEAD', credentials: 'same-origin' });
    return res.ok || (res.status >= 300 && res.status < 400) || res.status === 405;
  } catch {
    return false;
  }
}

export function TrainingView(): JSX.Element {
  const { t } = useI18n();
  const [state, setState] = useState<Ready>({ kind: 'loading' });
  // Ob die In-App-Modell-Registry als Overlay offen ist (Issue #266).
  const [registryOpen, setRegistryOpen] = useState(false);
  // Der aktuell konfigurierte GPU-/Trainings-Pod (Issue #222) — geheimnisfreie Kurzform aus /config.
  // undefined = noch nicht geladen; null = Backend liefert keinen Pod (ältere Backends/kaputte Konfig).
  const [pod, setPod] = useState<TrainingPodSummary | null | undefined>(undefined);
  // Ob der Pod-Editor (Issue #314) offen ist. Er bearbeitet die EIGENE Konfiguration.
  const [podEditorOpen, setPodEditorOpen] = useState(false);

  // Nach dem Speichern die Kurzform in der Leiste frisch ziehen, damit Anzeige und Stand
  // nicht auseinanderlaufen.
  async function reloadPodSummary(): Promise<void> {
    try {
      const config = await getConfig();
      setPod(config.trainingPod ?? null);
    } catch {
      // Die Leiste bleibt dann auf dem alten Stand — kein Grund, die Ansicht zu stören.
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let config;
      try {
        config = await getConfig();
      } catch {
        // Ohne /config-Endpunkt (ältere Backends) fällt die Ansicht auf den Default-Basispfad
        // zurück und prüft nur die Erreichbarkeit des Lab-Servers.
        config = { trainingEnabled: true, jupyterBasePath: '/jupyter' };
      }
      if (cancelled) return;
      setPod(config.trainingPod ?? null);
      if (!config.trainingEnabled) {
        setState({ kind: 'disabled' });
        return;
      }
      const labUrl = labUrlFromBase(config.jupyterBasePath);
      const reachable = await labReachable(labUrl);
      if (cancelled) return;
      setState(reachable ? { kind: 'ready', labUrl } : { kind: 'unavailable' });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative flex h-full flex-col" data-testid="training-view">
      {/* Erklär-Leiste: Kette Notebook → ONNX → Registry → Baustein + Registry-Knopf. */}
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-bf-border px-4 py-3">
        <div className="max-w-3xl">
          <PanelHeading>{t('training.heading')}</PanelHeading>
          <p className="text-sm text-bf-white">{t('training.intro')}</p>
          <p className="mt-1 text-xs text-bf-muted">{t('training.chain')}</p>
          {/* Sichtbarkeit des GPU-/Trainings-Pods in flow (Issue #222): der gewählte Stand
              (Provider/Konto/GPU/Region), nie die Zugangsdaten. */}
          {pod !== undefined && <TrainingPodBadge pod={pod} />}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button testId="training-pod-configure" onClick={() => setPodEditorOpen(true)}>
            {t('training.pod.configure')}
          </Button>
          <Button testId="training-registry-open" onClick={() => setRegistryOpen(true)}>
            {t('training.openRegistry')}
          </Button>
        </div>
      </div>

      {/* Zielparameter über dem Notebook (Issue #355): welche Optimierungsziele gelten und
          warum — plus der Start eines Trainings mit genau diesen Zielen. Steht bewusst ÜBER dem
          iframe: die Entscheidung, worauf trainiert wird, fällt vor dem Notebook, nicht darin. */}
      <TrainingGoalsPanel />

      {/* Editor der eigenen Pod-Konfiguration (Issue #314) als Overlay. */}
      {podEditorOpen && (
        <TrainingPodEditor
          onClose={() => setPodEditorOpen(false)}
          onSaved={() => {
            void reloadPodSummary();
          }}
        />
      )}

      {/* In-App-Modell-Registry als Overlay: lädt /api/v1/models über den authentifizierten Client
          (Issue #266) — kein roher Link mehr, der ohne Bearer-Header navigiert. */}
      {registryOpen && <RegistryView onClose={() => setRegistryOpen(false)} />}

      {/* Inhalt: iframe bei erreichbarem Lab, sonst ein sauberer Hinweis. */}
      <div className="min-h-0 flex-1">
        {state.kind === 'loading' && (
          <div className="p-8">
            <EmptyState title={t('training.loading')} />
          </div>
        )}

        {state.kind === 'disabled' && (
          <div className="p-8">
            <EmptyState title={t('training.disabledTitle')} hint={t('training.disabledHint')} />
          </div>
        )}

        {state.kind === 'unavailable' && (
          <div className="p-8">
            <EmptyState
              title={t('training.unavailableTitle')}
              hint={t('training.unavailableHint')}
            />
          </div>
        )}

        {state.kind === 'ready' && (
          <div className="flex h-full flex-col">
            <div className="flex justify-end px-4 py-1.5">
              <a
                href={state.labUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-bf-muted transition-colors hover:text-bf-gold"
              >
                {t('training.openInTab')}
              </a>
            </div>
            <iframe
              data-testid="training-iframe"
              title={t('training.frameTitle')}
              src={state.labUrl}
              className="min-h-0 w-full flex-1 border-0 bg-white"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Kleine Badge-Leiste mit dem konfigurierten GPU-/Trainings-Pod (Issue #222). Zeigt den gewählten
// Stand kompakt an; bei Provider 'local' (oder fehlendem Pod) den Hinweis auf den lokalen CPU-Pfad.
function TrainingPodBadge({ pod }: { pod: TrainingPodSummary | null }): JSX.Element {
  const { t } = useI18n();
  const isLocal = !pod || pod.provider === 'local';

  if (isLocal) {
    return (
      <p className="mt-2 text-xs text-bf-muted" data-testid="training-pod">
        <span className="font-medium text-bf-white">{t('training.pod.label')}:</span>{' '}
        {t('training.pod.none')}
      </p>
    );
  }

  const parts: string[] = [`${t('training.pod.provider')}: ${pod.provider}`];
  if (pod.account) parts.push(`${t('training.pod.account')}: ${pod.account}`);
  if (pod.gpuType) parts.push(`${t('training.pod.gpu')}: ${pod.gpuType} ×${pod.gpuCount}`);
  if (pod.region) parts.push(`${t('training.pod.region')}: ${pod.region}`);
  if (pod.cloudType) parts.push(`${t('training.pod.cloudType')}: ${pod.cloudType}`);
  parts.push(pod.hasSecrets ? t('training.pod.secrets') : t('training.pod.noSecrets'));

  return (
    <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-bf-muted" data-testid="training-pod">
      <span className="font-medium text-bf-white">{t('training.pod.label')}:</span>
      <span
        className={
          'rounded px-1.5 py-0.5 ' +
          (pod.enabled ? 'bg-bf-gold/20 text-bf-gold' : 'bg-bf-border/40 text-bf-muted')
        }
      >
        {pod.enabled ? t('training.pod.active') : t('training.pod.inactive')}
      </span>
      <span>{parts.join(' · ')}</span>
    </p>
  );
}


// ── Editor der eigenen GPU-/Trainings-Pod-Konfiguration (Issue #314) ──────────────────
//
// Seit #314 gehört die Pod-Konfiguration dem Nutzer, nicht der Instanz: jeder richtet hier seinen
// eigenen Pod ein (GET/PUT /training/pod, ohne Admin-Recht). Die Zugangsdaten stehen ausschließlich
// als OpenBao-Handle drin — der Schlüssel selbst wird nie im Browser eingegeben oder angezeigt.

// Die Provider aus dem Schema-Enum (schemas/training-pod.schema.json). 'local' ist der
// eingebettete CPU-Pfad ohne externen Pod.
const POD_PROVIDERS = ['local', 'runpod', 'aws', 'gcp', 'azure', 'lambda', 'paperspace'] as const;

// Auswahlfelder statt Freitext, wo die Wertemenge endlich ist (Issue #350) — und seit #368 je
// PROVIDER verschieden: eine RTX A6000 in EU-RO-1 gibt es bei RunPod, nicht bei AWS, und `local`
// (der eingebettete CPU-Pfad) hat weder GPU noch Region.
//
// Der Katalog kommt aus dem Backend (`GET /training/pod/options`) und wird EINMAL beim Öffnen des
// Editors geladen. Er ist Anbieterwissen, kein Darstellungsdetail — dasselbe Wissen steckt in den
// Alias-Tabellen, mit denen der RunPod-Adapter die GPU-Namen abbildet; zwei Kopien liefen
// unweigerlich auseinander.
//
// Die Listen unten sind nur noch der RÜCKFALL, falls der Abruf ausfällt: ein nicht erreichbarer
// Katalog darf niemanden aussperren.
const FALLBACK_OPTIONS: PodProviderOptions = {
  gpuTypes: [
    'NVIDIA RTX A6000',
    'NVIDIA RTX 4090',
    'NVIDIA A100 80GB PCIe',
    'NVIDIA H100 80GB HBM3',
  ],
  regions: [
    { value: 'US-TX-3', label: 'US West (TX-3)' },
    { value: 'US-TX-1', label: 'US East (TX-1)' },
    { value: 'EU-RO-1', label: 'EU-RO-1' },
    { value: 'EU-SE-1', label: 'EU-SE-1' },
  ],
  cloudTypes: ['SECURE_CLOUD', 'COMMUNITY_CLOUD'],
};

const LEERE_OPTIONEN: PodProviderOptions = { gpuTypes: [], regions: [], cloudTypes: [] };

// Hängt einen gespeicherten Wert an die Auswahl, wenn die Liste ihn nicht kennt. Ohne das würde
// ein Formular-Öffnen eine bestehende Konfiguration stillschweigend auf den ersten Listeneintrag
// umbiegen — der Nutzer speichert danach etwas anderes, als er vorgefunden hat.
function withCurrent(
  options: readonly { value: string; label: string }[],
  current: string,
): { value: string; label: string }[] {
  if (current === '' || options.some((o) => o.value === current)) return [...options];
  return [...options, { value: current, label: current }];
}

const asOptions = (values: readonly string[]): { value: string; label: string }[] =>
  values.map((v) => ({ value: v, label: v }));

// Ein OpenBao-Handle hat die Form bao:<pfad>#<feld>. Das Formular prüft das schon vor dem Senden,
// damit ein versehentlich eingefügter echter Schlüssel gar nicht erst das Gerät verlässt.
const BAO_HANDLE = /^bao:[A-Za-z0-9._/-]+#[A-Za-z0-9._-]+$/;

// Leere Eingabe → null (Feld entfernen), sonst die Zahl. Unsinnige Eingaben bleiben undefined
// und werden schlicht nicht mitgeschickt.
function numberOrNull(raw: string): number | null | undefined {
  if (raw.trim() === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

function TrainingPodEditor({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const [config, setConfig] = useState<TrainingPodConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Formularfelder als Strings — so bleibt „Feld geleert" von „Feld nicht angefasst" unterscheidbar.
  const [provider, setProvider] = useState('local');
  const [enabled, setEnabled] = useState(false);
  const [account, setAccount] = useState('');
  const [gpuType, setGpuType] = useState('');
  const [gpuCount, setGpuCount] = useState('1');
  const [region, setRegion] = useState('');
  const [cloudType, setCloudType] = useState('');
  // Anbieterkatalog (#368) — einmal geladen, danach lokal gefiltert. null = noch nicht da oder
  // nicht erreichbar; dann greift der Rückfall, damit das Formular bedienbar bleibt.
  const [optionen, setOptionen] = useState<Record<string, PodProviderOptions> | null>(null);
  // Zuletzt gesehener Provider — unterscheidet einen echten Wechsel vom Laden (siehe unten).
  const vorigerProvider = useRef<string | null>(null);
  const [cpuCores, setCpuCores] = useState('');
  const [memoryGb, setMemoryGb] = useState('');
  const [maxRuntimeMin, setMaxRuntimeMin] = useState('');
  const [maxCostEur, setMaxCostEur] = useState('');
  const [apiKeyHandle, setApiKeyHandle] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fehlschlag ist kein Drama: ohne Katalog greift FALLBACK_OPTIONS, und das Formular
        // bleibt bedienbar. Ein nicht erreichbarer Anbieterkatalog darf niemanden aussperren.
        const res = await getTrainingPodOptions();
        if (!cancelled) setOptionen(res.providers);
      } catch {
        if (!cancelled) setOptionen(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await getTrainingPod();
        if (cancelled) return;
        setConfig(loaded);
        setProvider(loaded.provider ?? 'local');
        // Merkstelle mitziehen: das LADEN der Konfiguration ist kein Providerwechsel. Ohne das
        // gälte der Sprung vom Anfangswert 'local' auf den gespeicherten Provider als Wechsel —
        // und die Bereinigung löschte genau die Werte, die gerade geladen wurden.
        vorigerProvider.current = loaded.provider ?? 'local';
        setEnabled(Boolean(loaded.enabled));
        setAccount(loaded.account ?? '');
        setGpuType(loaded.gpuType ?? '');
        setGpuCount(String(loaded.gpuCount ?? 1));
        setRegion(loaded.region ?? '');
        setCloudType(loaded.cloudType ?? '');
        setCpuCores(loaded.resources?.cpuCores != null ? String(loaded.resources.cpuCores) : '');
        setMemoryGb(loaded.resources?.memoryGb != null ? String(loaded.resources.memoryGb) : '');
        setMaxRuntimeMin(
          loaded.resources?.maxRuntimeMin != null ? String(loaded.resources.maxRuntimeMin) : '',
        );
        setMaxCostEur(
          loaded.resources?.maxCostEur != null ? String(loaded.resources.maxCostEur) : '',
        );
        setApiKeyHandle(loaded.secretRefs?.apiKey ?? '');
      } catch {
        if (!cancelled) setError(t('training.pod.loadFailed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Die Optionen des GEWÄHLTEN Providers. Kennt der Katalog ihn nicht (neuer Provider im Schema,
  // für den noch keine Liste gepflegt ist), bleiben die Listen leer und die Felder verhalten sich
  // wie zuvor — blockieren ist hier die falsche Antwort.
  const providerOptionen: PodProviderOptions = optionen
    ? (optionen[provider] ?? LEERE_OPTIONEN)
    : FALLBACK_OPTIONS;

  // Beim Provider-WECHSEL Werte leeren, die es beim neuen Anbieter nicht gibt. Sie stehen zu
  // lassen wäre schlimmer als sie zu verlieren: im Formular stünde dann eine Kombination, die es
  // nie gab, und beim Speichern wäre unklar, was gemeint war.
  //
  // Ausdrücklich NUR beim Wechsel, nicht beim Laden: ein gespeicherter Wert, den der Katalog
  // (noch) nicht kennt, muss erhalten bleiben — der Anbieterkatalog ändert sich schneller als
  // diese Tabelle, und das bloße Öffnen des Formulars darf eine bestehende Konfiguration nicht
  // verändern (Rückwärtskompatibilität aus #350).
  useEffect(() => {
    const vorher = vorigerProvider.current;
    vorigerProvider.current = provider;
    if (vorher === null || vorher === provider || !optionen) return;
    const opts = optionen[provider] ?? LEERE_OPTIONEN;
    setGpuType((v) => (v && !opts.gpuTypes.includes(v) ? '' : v));
    setRegion((v) => (v && !opts.regions.some((r) => r.value === v) ? '' : v));
    setCloudType((v) => (v && !opts.cloudTypes.includes(v) ? '' : v));
  }, [provider, optionen]);

  const isLocal = provider === 'local';
  const handleInvalid = apiKeyHandle.trim() !== '' && !BAO_HANDLE.test(apiKeyHandle.trim());

  async function save(): Promise<void> {
    if (handleInvalid) {
      setError(t('training.pod.handleInvalid'));
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    // 'local' verträgt weder GPU-Typ noch gpuCount > 1 — das weist das Backend fail-closed ab.
    // Deshalb hier gar nicht erst mitschicken, statt den Nutzer in einen 422 laufen zu lassen.
    const patch: TrainingPodUpdate = {
      enabled,
      provider,
      account: account.trim() === '' ? null : account.trim(),
      gpuType: isLocal || gpuType.trim() === '' ? null : gpuType.trim(),
      gpuCount: isLocal ? 1 : Math.max(1, Number(gpuCount) || 1),
      region: region.trim() === '' ? null : region.trim(),
      // Cloud-Art nur, wo der Anbieter sie kennt (#368) — sonst ausdrücklich löschen, damit
      // kein Rest einer früheren Konfiguration bei einem anderen Provider etwas behauptet.
      cloudType: cloudType.trim() === '' ? null : cloudType.trim(),
      secretRefs: apiKeyHandle.trim() === '' ? null : { apiKey: apiKeyHandle.trim() },
    };
    const resources = {
      cpuCores: numberOrNull(cpuCores),
      memoryGb: numberOrNull(memoryGb),
      maxRuntimeMin: numberOrNull(maxRuntimeMin),
      maxCostEur: numberOrNull(maxCostEur),
    };
    const anyLimit = Object.values(resources).some((v) => v != null);
    patch.resources = anyLimit ? resources : null;
    try {
      const result = await setTrainingPod(patch);
      setConfig(result);
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('training.pod.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="absolute inset-0 z-20 overflow-auto bg-bf-navy/95 p-6"
      data-testid="training-pod-editor"
    >
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <PanelHeading>{t('training.pod.editorTitle')}</PanelHeading>
            <p className="text-xs text-bf-muted">{t('training.pod.editorHint')}</p>
            {config?.owner && (
              <p className="mt-1 text-xs text-bf-muted" data-testid="training-pod-owner">
                {t('training.pod.owner')}: {config.owner}
              </p>
            )}
          </div>
          <Button testId="training-pod-close" onClick={onClose}>
            {t('training.pod.close')}
          </Button>
        </div>

        {error && (
          <p className="mb-3 rounded border border-red-500/60 px-3 py-2 text-sm text-red-300" role="alert">
            {error}
          </p>
        )}
        {saved && !error && (
          <p className="mb-3 text-sm text-bf-gold" data-testid="training-pod-saved">
            {t('training.pod.saved')}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <span data-testid="training-pod-provider">
            <LabeledSelect
              label={t('training.pod.provider')}
              value={provider}
              onChange={setProvider}
            >
              {POD_PROVIDERS.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </LabeledSelect>
          </span>
          <LabeledSelect
            label={t('training.pod.enabledLabel')}
            value={enabled ? 'on' : 'off'}
            onChange={(v) => setEnabled(v === 'on')}
          >
            <option value="off">{t('training.pod.inactive')}</option>
            <option value="on">{t('training.pod.active')}</option>
          </LabeledSelect>
          <LabeledInput label={t('training.pod.account')} value={account} onChange={setAccount} />
          <span data-testid="training-pod-region">
            <LabeledSelect label={t('training.pod.region')} value={region} onChange={setRegion}>
              <option value="">{t('training.pod.noneChosen')}</option>
              {withCurrent(providerOptionen.regions, region).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </LabeledSelect>
          </span>
          {!isLocal && (
            <>
              <span data-testid="training-pod-gputype">
                <LabeledSelect
                  label={t('training.pod.gpuType')}
                  value={gpuType}
                  onChange={setGpuType}
                >
                  <option value="">{t('training.pod.noneChosen')}</option>
                  {withCurrent(asOptions(providerOptionen.gpuTypes), gpuType).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </LabeledSelect>
              </span>
              <LabeledInput
                label={t('training.pod.gpuCount')}
                value={gpuCount}
                onChange={setGpuCount}
                type="number"
              />
            </>
          )}
          {/* Cloud-Art nur, wo der Anbieter sie kennt (#368) — nicht mehr an 'runpod'
              festgenagelt, sondern am Katalog: ein zweiter Anbieter mit dieser Unterscheidung
              bekäme das Feld dann ohne Codeänderung. */}
          {(providerOptionen.cloudTypes.length > 0 || cloudType !== '') && (
            <span data-testid="training-pod-cloudtype">
              <LabeledSelect
                label={t('training.pod.cloudType')}
                value={cloudType}
                onChange={setCloudType}
              >
                <option value="">{t('training.pod.noneChosen')}</option>
                {withCurrent(asOptions(providerOptionen.cloudTypes), cloudType).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </LabeledSelect>
            </span>
          )}
          <LabeledInput
            label={t('training.pod.cpuCores')}
            value={cpuCores}
            onChange={setCpuCores}
            type="number"
          />
          <LabeledInput
            label={t('training.pod.memoryGb')}
            value={memoryGb}
            onChange={setMemoryGb}
            type="number"
          />
          <LabeledInput
            label={t('training.pod.maxRuntimeMin')}
            value={maxRuntimeMin}
            onChange={setMaxRuntimeMin}
            type="number"
          />
          {/* Kostengrenze bleibt Freitext — dort gibt es keine endliche Wertemenge (#350). */}
          <span data-testid="training-pod-maxcost">
            <LabeledInput
              label={t('training.pod.maxCostEur')}
              value={maxCostEur}
              onChange={setMaxCostEur}
              type="number"
            />
          </span>
        </div>

        <div className="mt-3">
          <LabeledInput
            label={t('training.pod.apiKeyHandle')}
            value={apiKeyHandle}
            onChange={setApiKeyHandle}
            placeholder="bao:apps/training-pod#runpod_api_key"
          />
          <p className="mt-1 text-xs text-bf-muted">{t('training.pod.apiKeyHint')}</p>
          {handleInvalid && (
            <p className="mt-1 text-xs text-red-300" data-testid="training-pod-handle-invalid">
              {t('training.pod.handleInvalid')}
            </p>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <Button
            variant="primary"
            testId="training-pod-save"
            disabled={saving || handleInvalid}
            onClick={() => {
              void save();
            }}
          >
            {saving ? t('training.pod.saving') : t('training.pod.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
